import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "../ctx";
import { Info } from "lucide-react";
import { PulseDot, PulseWord } from "@/components/pulse/PulseDot";
import { SummaryPanel } from "@/components/pulse/SummaryPanel";
import { HowPulseWorks } from "@/components/pulse/HowPulseWorks";
import { bandOf, movementLine, fmtTime } from "@/components/pulse/pulse";
import { PulseChip } from "@/components/pulse/PulseChip";

const CARD = "bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden";

export function PulseCountsLine({ counts }) {
  if (!counts) return null;
  const parts = [["red", "#D32F2F"], ["yellow", "#D97706"], ["green", "#16A34A"]].filter(([k]) => counts[k] > 0);
  return (
    <span className="inline-flex flex-wrap gap-3.5 text-[13px] font-bold text-[#57534E]" data-testid="pulse-counts">
      {parts.map(([k, c]) => <span key={k}><i className="inline-block h-[9px] w-[9px] rounded-full mr-1.5 align-middle" style={{ background: c }} />{counts[k]} {k}</span>)}
      {counts.quiet > 0 && <span className="text-[#8C827A]">{counts.quiet} quiet</span>}
      {counts.off > 0 && <span className="text-[#A8A29E]">{counts.off} not scored</span>}
    </span>
  );
}

export function PulseRow({ row, open, onToggle, children, right }) {
  const band = bandOf(row);
  const s = row.snapshot;
  const scored = band !== "off" && band !== "quiet";
  const Status = ({ ids = false }) => (
    <>
      <PulseWord band={band} testid={ids ? `pulse-word-${row.tenant_id}` : undefined} />
      {s?.expected && <span className="ml-1.5 text-[10px] font-bold text-[#8C827A] uppercase tracking-wide" data-testid={ids ? `pulse-expected-mini-${row.tenant_id}` : undefined}>· expected</span>}
      {s?.falling && <span className="ml-1.5 text-[10px] font-extrabold text-[#B91C1C] uppercase tracking-wide" data-testid={ids ? `pulse-falling-${row.tenant_id}` : undefined}>falling</span>}
      <div className="text-xs font-bold text-[#8C827A] mt-0.5 truncate" data-testid={ids ? `pulse-move-${row.tenant_id}` : undefined}>{movementLine(row)}</div>
    </>
  );
  return (
    <div data-testid={`pulse-row-${row.tenant_id}`} className={`grid grid-cols-[20px_1fr_auto] sm:grid-cols-[24px_1fr_200px_150px] gap-3 sm:gap-4 items-center px-4 sm:px-5 py-3.5 sm:py-4 border-b border-[#F3EFE9] last:border-0 ${open ? "bg-[#FAF8F5]" : ""} ${band === "off" ? "opacity-60" : ""}`}>
      <PulseDot band={band} quiet={!!s?.expected} testid={`pulse-dot-${row.tenant_id}`} />
      <div className="min-w-0">
        <div className="font-bold text-[15px] text-[#1C1917] truncate">{row.name}</div>
        <div className="text-xs text-[#8C827A] mt-0.5 truncate">{row.manager || (row.homes ? `${row.homes} properties` : "No manager assigned")}</div>
        <div className="sm:hidden mt-1"><Status /></div>
      </div>
      <div className="hidden sm:block min-w-0"><Status ids /></div>
      <div className="justify-self-end">
        {right !== undefined ? right : scored ? (
          <button type="button" data-testid={`pulse-summary-btn-${row.tenant_id}`} onClick={onToggle}
            className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${open ? "bg-ink text-white border-ink" : "bg-white border-[#E8E2D9] text-[#1C1917] hover:border-ink"}`}>
            Summary
          </button>
        ) : <span />}
      </div>
      {open && children}
    </div>
  );
}

export default function PortfolioPulse() {
  const { viewCompanyId, enterCommunity, role, tenants, refreshTenants } = useDash();
  const q = viewCompanyId ? `?company_id=${viewCompanyId}` : "";
  const [data, setData] = useCachedState(`pulse-pf:${viewCompanyId || ""}`, null);
  const [loading, setLoading] = useState(!data);
  const [openId, setOpenId] = useState(null);
  const [how, setHow] = useState({ open: false, id: null });
  const load = useCallback(() => api.get(`/pulse/portfolio${q}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)), [q, setData]);
  useEffect(() => { load(); }, [load]);
  const rows = data?.communities || [];
  const canExpected = role === "company_admin" || role === "regional_manager" || role === "super_admin" || role === "platform_admin";

  return (
    <div className="max-w-[1100px] space-y-5" data-testid="portfolio-pulse">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <PageTitle title="Pulse" about={<>How every community is doing. Updated each morning{data?.updated_at ? ` · ${fmtTime(data.updated_at)}` : ""}.</>} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PulseCountsLine counts={data?.counts} />
          <button type="button" data-testid="pulse-how-btn" onClick={() => setHow({ open: true, id: openId })}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E2D9] bg-white px-3 py-1.5 text-[13px] font-bold text-[#1C1917] hover:border-ink">
            <Info className="h-4 w-4" strokeWidth={2} /> How Pulse works
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className={`${CARD} p-10 text-center text-[#78716C]`} data-testid="pulse-empty">No communities in this portfolio yet.</div>
      ) : (
        <div className={CARD} data-testid="pulse-list">
          {rows.map((r) => (
            <PulseRow key={r.tenant_id} row={r} open={openId === r.tenant_id} onToggle={() => setOpenId(openId === r.tenant_id ? null : r.tenant_id)}
              right={!r.pulse_on ? <PulseChip tenant={tenants.find((t) => t.id === r.tenant_id) || { id: r.tenant_id, name: r.name }} role={role} onTurnedOn={async () => { await refreshTenants(); load(); }} /> : undefined}>
              <SummaryPanel tenantId={r.tenant_id} name={r.name} snapshot={r.snapshot} canExpected={canExpected}
                onWhy={() => setHow({ open: true, id: r.tenant_id })} onOpen={() => enterCommunity(r.tenant_id)} onExpectedChange={load} />
            </PulseRow>
          ))}
        </div>
      )}
      {data?.counts?.off > 0 && (
        <p className="text-xs text-[#8C827A]" data-testid="pulse-all-off">Pulse is an add-on per community — "Not scored" rows can be turned on right here{role === "company_admin" ? " when Pulse is included in your plan" : ""}.</p>
      )}
      <HowPulseWorks open={how.open} onOpenChange={(o) => setHow((h) => ({ ...h, open: o }))} rows={rows} selectedId={how.id}
        settings={data?.settings} signals={data?.signals} canTune={!!data?.can_tune} mode="company" companyId={viewCompanyId || data?.company_id} onSaved={load} />
    </div>
  );
}
